import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { FORMS } from "@/config/forms";
import { fmtMarks } from "@/lib/scoring";
import { AssessmentForm } from "@/components/AssessmentForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useSortableData, SortableHead } from "@/hooks/useSortableData";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Search, Eye, Trash2, Inbox, Check, X, RotateCcw } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function CategoryPage() {
  const { key } = useParams();
  const { user, hasPerm } = useAuth();
  const form = FORMS[key];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [rejectItem, setRejectItem] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [editItem, setEditItem] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  const load = () => {
    setLoading(true);
    api.get(`/assessments?category=${key}`).then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [key]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    let list = statusFilter === "all" ? items : items.filter((it) => (it.status || "pending") === statusFilter);
    if (!q) return list;
    return list.filter((it) => {
      const d = it.data || {};
      const m = d.municipality || {};
      return [m.district, m.name, m.type, d.assessor_name, d.area_name, d.ward_no, it.created_by_name].some((v) => (v || "").toString().toLowerCase().includes(q));
    });
  }, [items, search, statusFilter]);

  const sort = useSortableData(filtered);
  const sorted = sort.sorted;

  const doDelete = async () => {
    try {
      await api.delete(`/assessments/${deleteId}`);
      toast.success("Deleted");
      setDeleteId(null);
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.put(`/assessments/${id}/status`, { status });
      toast.success(status === "approved" ? "Approved" : status === "rejected" ? "Rejected" : "Updated");
      await load();
      window.dispatchEvent(new Event("pending-refresh"));
    } catch {
      toast.error("Action failed");
    }
  };

  const doReject = async () => {
    try {
      await api.put(`/assessments/${rejectItem.id}/status`, { status: "rejected", reason: rejectReason });
      toast.success("Rejected");
      setRejectItem(null); setRejectReason("");
      await load();
      window.dispatchEvent(new Event("pending-refresh"));
    } catch {
      toast.error("Action failed");
    }
  };

  const statusBadge = (s) => {
    const map = { approved: "bg-emerald-100 text-emerald-700", pending: "bg-amber-100 text-amber-700", rejected: "bg-red-100 text-red-700" };
    return <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium capitalize ${map[s] || map.pending}`}>{s || "pending"}</span>;
  };

  if (!form) return <div className="text-slate-500">Unknown category.</div>;

  const hasMarks = !!form.totalMarks;

  const renderValue = (field, val) => {
    if (val === undefined || val === null || val === "") return <span className="text-slate-400">—</span>;
    if (field.type === "image") return <img src={fileUrl(val)} alt={field.label} className="h-24 rounded-md border border-slate-200 object-cover" />;
    if (field.type === "municipality") return <span>{[val.district, val.name, val.type].filter(Boolean).join(" / ") || "—"}</span>;
    if (field.type === "geotag") return <span>{val.lat}, {val.lng}</span>;
    if (field.type === "checkbox") return <span>{(val || []).join(", ")}</span>;
    return <span className="break-words">{val.toString()}</span>;
  };

  return (
    <div>
      <div className="flex justify-between items-start mb-6 gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-600 font-medium">Assessment</p>
          <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">{form.title}</h1>
        </div>
        {hasPerm("assessments.create") && (
          <Button onClick={() => setShowForm(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-new-btn">
            <Plus className="h-4 w-4 mr-2" /> Add New
          </Button>
        )}
      </div>

      <Card className="border-slate-200 shadow-sm">
        <div className="p-4 border-b border-slate-200 flex flex-wrap items-center gap-3">
          <div className="relative max-w-sm flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by assessor, area, ward..." className="pl-9" data-testid="search-input" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700" data-testid="status-filter">
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-14">S.No</TableHead>
                <SortableHead column="data.municipality.district" sort={sort}>District</SortableHead>
                <SortableHead column="data.municipality.name" sort={sort}>Municipality</SortableHead>
                <SortableHead column="data.municipality.type" sort={sort}>Type</SortableHead>
                <SortableHead column="data.assessor_name" sort={sort}>Assessor</SortableHead>
                <SortableHead column="data.area_name" sort={sort}>Area</SortableHead>
                <SortableHead column="data.ward_no" sort={sort}>Ward No.</SortableHead>
                <SortableHead column="status" sort={sort}>Status</SortableHead>
                {hasMarks && <SortableHead column="score" sort={sort}>Marks</SortableHead>}
                {hasPerm("assessments.view_all") && <SortableHead column="created_by_name" sort={sort}>Submitted By</SortableHead>}
                <SortableHead column="created_at" sort={sort}>Date</SortableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={10 + (hasMarks ? 1 : 0) + (hasPerm("assessments.view_all") ? 1 : 0)} className="text-center py-10 text-slate-400">Loading...</TableCell></TableRow>
              ) : sorted.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10 + (hasMarks ? 1 : 0) + (hasPerm("assessments.view_all") ? 1 : 0)} className="text-center py-12">
                    <Inbox className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500">No assessments yet. Click "Add New" to submit one.</p>
                  </TableCell>
                </TableRow>
              ) : (
                sorted.map((it, idx) => (
                  <TableRow key={it.id} className="hover:bg-slate-50" data-testid={`row-${it.id}`}>
                    <TableCell className="text-slate-500">{idx + 1}</TableCell>
                    <TableCell className="capitalize">{(it.data?.municipality?.district || "—").toLowerCase()}</TableCell>
                    <TableCell className="font-medium">{it.data?.municipality?.name || "—"}</TableCell>
                    <TableCell className="text-xs text-slate-600">{it.data?.municipality?.type || "—"}</TableCell>
                    <TableCell>{it.data?.assessor_name || "—"}</TableCell>
                    <TableCell>{it.data?.area_name || "—"}</TableCell>
                    <TableCell>{it.data?.ward_no || "—"}</TableCell>
                    <TableCell data-testid={`status-${it.id}`}>
                      {statusBadge(it.status)}
                      {it.status === "rejected" && it.rejection_reason && <p className="text-[11px] text-red-500 mt-1 max-w-[160px] truncate" title={it.rejection_reason}>{it.rejection_reason}</p>}
                    </TableCell>
                    {hasMarks && (
                      <TableCell data-testid={`marks-${it.id}`}>
                        <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 font-semibold px-2.5 py-1 text-xs">
                          {it.score != null ? `${fmtMarks(it.score)} / ${fmtMarks(it.max_score)}` : "—"}
                        </span>
                      </TableCell>
                    )}
                    {hasPerm("assessments.view_all") && <TableCell>{it.created_by_name}</TableCell>}
                    <TableCell>{new Date(it.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {it.status === "rejected" && it.created_by === user?.id && hasPerm("assessments.create") && (
                        <Button variant="ghost" size="sm" onClick={() => setEditItem(it)} title="Edit & Resubmit" data-testid={`resubmit-${it.id}`}><RotateCcw className="h-4 w-4 text-emerald-600" /></Button>
                      )}
                      {hasPerm("assessments.approve") && it.status !== "approved" && (
                        <Button variant="ghost" size="sm" onClick={() => setStatus(it.id, "approved")} title="Approve" data-testid={`approve-${it.id}`}><Check className="h-4 w-4 text-emerald-600" /></Button>
                      )}
                      {hasPerm("assessments.approve") && it.status !== "rejected" && (
                        <Button variant="ghost" size="sm" onClick={() => { setRejectItem(it); setRejectReason(""); }} title="Reject" data-testid={`reject-${it.id}`}><X className="h-4 w-4 text-amber-600" /></Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => setViewItem(it)} data-testid={`view-${it.id}`}><Eye className="h-4 w-4" /></Button>
                      {hasPerm("assessments.delete") && (
                        <Button variant="ghost" size="sm" onClick={() => setDeleteId(it.id)} data-testid={`delete-${it.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {showForm && <AssessmentForm categoryKey={key} open={showForm} onClose={() => setShowForm(false)} onSaved={load} />}
      {editItem && <AssessmentForm categoryKey={key} open={!!editItem} assessment={editItem} onClose={() => setEditItem(null)} onSaved={load} />}

      <Dialog open={!!rejectItem} onOpenChange={(o) => !o && setRejectItem(null)}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader><DialogTitle className="font-heading">Reject Assessment</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            <label className="text-sm text-slate-600">Reason for rejection (visible to the assessor)</label>
            <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Explain what needs to be corrected..." rows={4} data-testid="reject-reason-input" />
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setRejectItem(null)} data-testid="reject-cancel">Cancel</Button>
            <Button onClick={doReject} className="bg-red-500 hover:bg-red-600 text-white" data-testid="reject-confirm">Reject</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewItem} onOpenChange={(o) => !o && setViewItem(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="view-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{form.title}</DialogTitle>
            {hasMarks && viewItem && (
              <div className="text-sm text-slate-600" data-testid="view-score">
                Total Score: <span className="font-bold text-emerald-700">{viewItem.score != null ? fmtMarks(viewItem.score) : "—"}</span>
                <span className="text-slate-400"> / {form.totalMarks}</span>
              </div>
            )}
          </DialogHeader>
          {viewItem && (
            <div className="space-y-4">
              {form.fields.map((f) => (
                <div key={f.name} className="border-b border-slate-100 pb-3">
                  <p className="text-xs font-medium text-slate-500 mb-1">{f.label}</p>
                  <div className="text-sm text-slate-800">{renderValue(f, viewItem.data?.[f.name])}</div>
                </div>
              ))}
              <div className="rounded-md bg-slate-50 border border-slate-200 p-4 space-y-1.5" data-testid="view-approval-history">
                <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-1">Approval History</p>
                <div className="text-sm flex items-center gap-2">Status: {statusBadge(viewItem.status)}</div>
                <p className="text-sm text-slate-600">Submitted by <span className="font-medium text-slate-800">{viewItem.created_by_name}</span> on {new Date(viewItem.created_at).toLocaleString()}</p>
                {viewItem.status === "pending" && <p className="text-sm text-amber-600">Awaiting approval.</p>}
                {viewItem.status !== "pending" && viewItem.approved_by_name && (
                  <p className="text-sm text-slate-700">{viewItem.status === "approved" ? "Approved" : "Rejected"} by <span className="font-medium">{viewItem.approved_by_name}</span>{viewItem.approved_at ? ` on ${new Date(viewItem.approved_at).toLocaleString()}` : ""}</p>
                )}
                {viewItem.status === "rejected" && viewItem.rejection_reason && <p className="text-sm text-red-600">Reason: {viewItem.rejection_reason}</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this assessment?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-500 hover:bg-red-600" data-testid="delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
